import {
    Badge,
    Button,
    Collapse,
    Divider,
    Group,
    Loader,
    Paper,
    SegmentedControl,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useAgentStore } from "@/sdk/agent/store/agent.store";
import { userCache } from "@/shared-utils";

import {
    buildTranscriptEventPayload,
    DEFAULT_REALTIME_TOOLS,
    fetchMockConversations,
    type MockConversation,
    type MockConversationSnippet,
    type Speaker,
    submitTranscriptEvent,
} from "./MockTranscriptEventsTab.helpers";
import { type PlaybackSpeed, useMockPlayback } from "./useMockPlayback";

type MockTranscriptEventsTabProps = {
    partner: string;
    sessionId: string;
};

interface FormValues {
    transcript: string;
    speaker: Speaker;
    client: string;
    realtimeTools: string;
}

export function MockTranscriptEventsTab({ partner, sessionId }: MockTranscriptEventsTabProps) {
    const agentName = useAgentStore((state) => state.agentName);
    const agentId = userCache.userPrincipalName ?? agentName;
    const employeeId = userCache.employeeId ?? "999999";

    const form = useForm<FormValues>({
        validateInputOnBlur: true,
        initialValues: {
            transcript: "",
            speaker: "expert",
            client: partner,
            realtimeTools: DEFAULT_REALTIME_TOOLS,
        },
        validate: {
            transcript: (value) => (value.trim() === "" ? "Transcript required" : null),
            client: (value) => (value.trim() === "" ? "Client required" : null),
            realtimeTools: (value) => (value.trim() === "" ? "Realtime tools required" : null),
        },
    });

    const mutation = useMutation({
        mutationFn: (values: FormValues) =>
            submitTranscriptEvent(
                buildTranscriptEventPayload({
                    sessionId,
                    agentId,
                    employeeId,
                    partner,
                    client: values.client,
                    transcript: values.transcript,
                    realtimeTools: values.realtimeTools,
                    speaker: values.speaker,
                }),
            ),
        onSuccess: () => {
            showNotification({
                id: "twilio-real-time-success",
                position: "top-center",
                withCloseButton: true,
                autoClose: 3000,
                title: "Transcript event sent",
                message: "Produced to platform.ingest.app.twilio-real-time",
                color: "green",
            });
            form.setFieldValue("transcript", "");
        },
        onError: (error: Error) => {
            showNotification({
                id: "twilio-real-time-error",
                position: "top-center",
                withCloseButton: true,
                autoClose: 5000,
                title: "Failed to send transcript event",
                message: error.message,
                color: "red",
            });
        },
    });

    const handleSubmit = form.onSubmit((values) => mutation.mutate(values));

    const conversationsQuery = useQuery({
        queryKey: ["mock-transcript-conversations"],
        queryFn: fetchMockConversations,
        staleTime: 5 * 60 * 1000,
    });

    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [speed, setSpeed] = useState<PlaybackSpeed>(1);

    const playback = useMockPlayback({
        sendSnippet: (snippet) =>
            submitTranscriptEvent(
                buildTranscriptEventPayload({
                    sessionId,
                    agentId,
                    employeeId,
                    partner,
                    client: form.values.client,
                    realtimeTools: form.values.realtimeTools,
                    transcript: snippet.transcript,
                    speaker: snippet.speaker,
                }),
            ),
        onError: (error) => {
            showNotification({
                id: "twilio-real-time-playback-error",
                position: "top-center",
                withCloseButton: true,
                autoClose: 5000,
                title: "Mock conversation stopped",
                message: error.message,
                color: "red",
            });
        },
    });

    const handleSpeedChange = (next: PlaybackSpeed) => {
        setSpeed(next);
        if (playback.state.kind !== "idle") {
            playback.setSpeed(next);
        }
    };

    const handlePlay = (convo: MockConversation) => {
        setExpandedId(convo.id);
        playback.play(convo, speed);
    };

    const snippetMutation = useMutation({
        mutationFn: (snippet: MockConversationSnippet) =>
            submitTranscriptEvent(
                buildTranscriptEventPayload({
                    sessionId,
                    agentId,
                    employeeId,
                    partner,
                    client: form.values.client,
                    realtimeTools: form.values.realtimeTools,
                    transcript: snippet.transcript,
                    speaker: snippet.speaker,
                }),
            ),
        onSuccess: () => {
            showNotification({
                id: "twilio-real-time-snippet-success",
                position: "top-center",
                withCloseButton: true,
                autoClose: 2000,
                title: "Snippet sent",
                message: "Produced to platform.ingest.app.twilio-real-time",
                color: "green",
            });
        },
        onError: (error: Error) => {
            showNotification({
                id: "twilio-real-time-snippet-error",
                position: "top-center",
                withCloseButton: true,
                autoClose: 5000,
                title: "Failed to send snippet",
                message: error.message,
                color: "red",
            });
        },
    });

    const activePlayback = playback.state.kind === "idle" ? null : playback.state;
    const playbackActive = activePlayback !== null;

    return (
        <Stack gap="sm">
            <form onSubmit={handleSubmit}>
                <Stack gap="sm">
                    <Textarea
                        label="Transcript"
                        placeholder="What was said..."
                        autosize
                        minRows={2}
                        maxRows={6}
                        size="xs"
                        {...form.getInputProps("transcript")}
                    />
                    <Select
                        label="Speaker"
                        size="xs"
                        data={[
                            { value: "expert", label: "Expert" },
                            { value: "customer", label: "Customer" },
                        ]}
                        comboboxProps={{ zIndex: 10000 }}
                        allowDeselect={false}
                        {...form.getInputProps("speaker")}
                    />
                    <TextInput label="Client" size="xs" {...form.getInputProps("client")} />
                    <TextInput label="Realtime tools" size="xs" {...form.getInputProps("realtimeTools")} />
                    <Text size="xs" c="dimmed">
                        Session: {sessionId || "—"} · Agent: {agentId} · Partner: {partner.toLowerCase()}
                    </Text>
                    <Button
                        type="submit"
                        size="xs"
                        variant="light"
                        color="grape"
                        loading={mutation.isPending}
                        disabled={!sessionId}
                    >
                        Send transcript event
                    </Button>
                </Stack>
            </form>

            <Divider label="Pre-built conversations" labelPosition="center" />

            {conversationsQuery.isPending ? (
                <Group justify="center" py="xs">
                    <Loader size="xs" />
                </Group>
            ) : conversationsQuery.isError ? (
                <Text size="xs" c="red">
                    Failed to load conversations: {conversationsQuery.error.message}
                </Text>
            ) : conversationsQuery.data.length === 0 ? (
                <Text size="xs" c="dimmed">
                    No mock conversations available.
                </Text>
            ) : (
                <Stack gap="xs">
                    <Group gap="xs" justify="flex-end">
                        <Text size="xs" c="dimmed">
                            Speed
                        </Text>
                        <SegmentedControl
                            size="xs"
                            value={String(speed)}
                            onChange={(value) => handleSpeedChange(Number(value) as PlaybackSpeed)}
                            data={[
                                { value: "1", label: "1x" },
                                { value: "2", label: "2x" },
                                { value: "4", label: "4x" },
                            ]}
                        />
                    </Group>
                    {conversationsQuery.data.map((convo) => {
                        const isActive = activePlayback?.conversation.id === convo.id;
                        return (
                            <ConversationCard
                                key={convo.id}
                                convo={convo}
                                expanded={expandedId === convo.id}
                                onToggleExpanded={() => setExpandedId((prev) => (prev === convo.id ? null : convo.id))}
                                onPlay={() => handlePlay(convo)}
                                onPause={playback.pause}
                                onResume={playback.resume}
                                onStop={playback.stop}
                                playbackKind={isActive ? activePlayback.kind : "idle"}
                                currentSentIndex={isActive ? activePlayback.sentIndex : null}
                                playDisabled={playbackActive || !sessionId}
                                onSendSnippet={(snippet) => snippetMutation.mutate(snippet)}
                                sendingSnippet={snippetMutation.isPending ? snippetMutation.variables : undefined}
                                sendDisabled={snippetMutation.isPending || playbackActive || !sessionId}
                            />
                        );
                    })}
                </Stack>
            )}
        </Stack>
    );
}

type CardPlaybackKind = "idle" | "playing" | "paused";

interface ConversationCardProps {
    convo: MockConversation;
    expanded: boolean;
    onToggleExpanded: () => void;
    onPlay: () => void;
    onPause: () => void;
    onResume: () => void;
    onStop: () => void;
    playbackKind: CardPlaybackKind;
    currentSentIndex: number | null;
    playDisabled: boolean;
    onSendSnippet: (snippet: MockConversationSnippet) => void;
    sendingSnippet: MockConversationSnippet | undefined;
    sendDisabled: boolean;
}

function ConversationCard({
    convo,
    expanded,
    onToggleExpanded,
    onPlay,
    onPause,
    onResume,
    onStop,
    playbackKind,
    currentSentIndex,
    playDisabled,
    onSendSnippet,
    sendingSnippet,
    sendDisabled,
}: ConversationCardProps) {
    const isActive = playbackKind !== "idle";

    return (
        <Paper p="xs" withBorder>
            <Stack gap={4}>
                <Text fw={600} size="xs">
                    {convo.name}
                </Text>
                <Text size="xs" c="dimmed">
                    {convo.description}
                </Text>
                <Group gap="xs" justify="space-between" wrap="nowrap">
                    <Badge size="xs" variant="light">
                        {isActive && currentSentIndex !== null
                            ? `${currentSentIndex + 1} / ${convo.snippets.length}`
                            : `${convo.snippets.length} snippets`}
                    </Badge>
                    <Group gap={4} wrap="nowrap">
                        <Button size="compact-xs" variant="subtle" onClick={onToggleExpanded}>
                            {expanded ? "Hide" : "Preview"}
                        </Button>
                        {playbackKind === "playing" && (
                            <Button size="compact-xs" variant="light" color="gray" onClick={onPause}>
                                Pause
                            </Button>
                        )}
                        {playbackKind === "paused" && (
                            <Button size="compact-xs" variant="light" color="grape" onClick={onResume}>
                                Resume
                            </Button>
                        )}
                        {isActive ? (
                            <Button size="compact-xs" variant="light" color="red" onClick={onStop}>
                                Stop
                            </Button>
                        ) : (
                            <Button
                                size="compact-xs"
                                variant="light"
                                color="grape"
                                onClick={onPlay}
                                disabled={playDisabled}
                            >
                                Play
                            </Button>
                        )}
                    </Group>
                </Group>
                <Collapse expanded={expanded}>
                    <Stack gap={4} mt={4}>
                        {convo.snippets.map((snippet, index) => {
                            const isSent = isActive && currentSentIndex !== null && index <= currentSentIndex;
                            return (
                                <Group key={index} gap="xs" wrap="nowrap" align="flex-start">
                                    <Text
                                        size="xs"
                                        style={{
                                            flex: 1,
                                            opacity: isSent ? 0.4 : 1,
                                            transition: "opacity 200ms ease",
                                        }}
                                    >
                                        <Text span c={snippet.speaker === "expert" ? "grape" : "blue"} fw={500}>
                                            {snippet.speaker}:
                                        </Text>{" "}
                                        {snippet.transcript}
                                    </Text>
                                    <Button
                                        size="compact-xs"
                                        variant="subtle"
                                        onClick={() => onSendSnippet(snippet)}
                                        loading={sendingSnippet === snippet}
                                        disabled={sendDisabled}
                                    >
                                        Send
                                    </Button>
                                </Group>
                            );
                        })}
                    </Stack>
                </Collapse>
            </Stack>
        </Paper>
    );
}
